import { Moon, Sun } from "lucide-react";
import { companyNavigation } from "../lib/navigation";
import SubscribeModal from "./SubscribeModal";
import { titleCaseHeadline } from "../utils/titleCase";
import AdSlot from "./AdSlot";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, X, Bookmark, MailCheck, Facebook, Instagram, Twitter, Youtube } from "lucide-react";
import { NAV, NAV_MORE, SITE, getStoryById } from "../data/stories";
import { getSavedStories, removeSavedStory } from "../utils/saved";
import { fetchNewsletterPrefs, saveNewsletterPrefs } from "../api";
import { useAuth } from "../context/AuthContext";
import { initialsOf } from "./AuthorPage";
import NavigationCurtain, { MenuIcon, SearchIcon } from "./NavigationCurtain";

const DIGESTS = [
  { key: "daily", label: "The Standard Daily", hint: "Today’s headlines, every morning" },
  { key: "breaking", label: "Breaking news alerts", hint: "Only when a big story breaks" },
  { key: "sports", label: "Sports roundup", hint: "The weekend in Gambian sport" },
];

const Socials = ({ className = "" }) => (
  <div className={`flex items-center gap-4 ${className}`} aria-label="Social media">
    <a aria-label="Facebook" href={SITE.socials.facebook} target="_blank" rel="noreferrer" className="hover:text-[#eeee22] transition-colors"><Facebook size={15} /></a>
    <a aria-label="Instagram" href="https://www.instagram.com/standardgambia/" target="_blank" rel="noreferrer" className="hover:text-[#eeee22] transition-colors"><Instagram size={15} /></a>
    <a aria-label="X" href={SITE.socials.x} target="_blank" rel="noreferrer" className="hover:text-[#eeee22] transition-colors"><Twitter size={15} /></a>
    <a aria-label="YouTube" href={SITE.socials.youtube} target="_blank" rel="noreferrer" className="hover:text-[#eeee22] transition-colors"><Youtube size={16} /></a>
  </div>
);

export const Masthead = () => {
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [compact, setCompact] = useState(false);
  const [theme, setTheme] = useState('light');
  const sentinelRef = useRef(null);
  useEffect(() => {
    const syncTheme = () => setTheme(document.documentElement.classList.contains('dark') ? 'dark' : 'light');
    syncTheme();
    window.addEventListener('standard-theme-change', syncTheme);
    window.addEventListener('storage', syncTheme);
    const desktop = window.matchMedia('(min-width: 1280px)');
    const update = () => setCompact(!desktop.matches && sentinelRef.current?.getBoundingClientRect().top < 0);
    const observer = new IntersectionObserver(update, {threshold: 0});
    if (sentinelRef.current) observer.observe(sentinelRef.current);
    desktop.addEventListener('change', update);
    update();
    return () => { observer.disconnect(); desktop.removeEventListener('change', update); window.removeEventListener('standard-theme-change', syncTheme); window.removeEventListener('storage', syncTheme); };
  }, []);
  function toggleCompactTheme() {
    const next = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.classList.toggle('dark', next === 'dark');
    try { localStorage.setItem('std-theme', next); } catch {}
    window.dispatchEvent(new Event('standard-theme-change'));
  }
  const [curtainTop, setCurtainTop] = useState(124);
  const [focusSearch, setFocusSearch] = useState(false);
  const headerRef = useRef(null), triggerRef = useRef(null), searchRef = useRef(null), mobileTriggerRef = useRef(null), mobileSearchRef = useRef(null);
  useEffect(() => {
    if (!drawerOpen || !headerRef.current) return;
    const observer = new ResizeObserver(() => setCurtainTop(Math.max(0, headerRef.current?.getBoundingClientRect().bottom || 54)));
    observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, [drawerOpen]);
  const closeMenu = useCallback(() => setDrawerOpen(false), []);
  const toggleMenu = (search = false) => { setCurtainTop(Math.max(0, headerRef.current?.getBoundingClientRect().bottom || 124)); setFocusSearch(search); setDrawerOpen(open => !open); };
  useEffect(() => {
    const onSearch = () => { setCurtainTop(Math.max(0, headerRef.current?.getBoundingClientRect().bottom || 124)); setFocusSearch(true); setDrawerOpen(open=>!open); };
    window.addEventListener('std-open-search', onSearch);
    const update = () => setCurtainTop(Math.max(0, headerRef.current?.getBoundingClientRect().bottom || 124));
    window.addEventListener('resize', update);
    return () => { window.removeEventListener('std-open-search', onSearch); window.removeEventListener('resize', update); };
  }, []);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const closeSubscribe = useCallback(() => setSubscribeOpen(false), []);
  const [accountOpen, setAccountOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null), morePanelRef = useRef(null), moreTriggerRef = useRef(null), moreHoverTimer = useRef(null);
  const [moreLeft, setMoreLeft] = useState(16);
  const closeMoreSoon = () => { clearTimeout(moreHoverTimer.current); moreHoverTimer.current = setTimeout(() => setMoreOpen(false), 220); };
  useEffect(() => {
    if (!moreOpen) return;
    const position = () => {
      const trigger = moreTriggerRef.current?.getBoundingClientRect(), header = headerRef.current?.getBoundingClientRect();
      if (trigger && header) setMoreLeft(Math.max(16, Math.min(trigger.right - header.left, header.width - 16) - Math.min(540, header.width - 32)));
    };
    position(); window.addEventListener('resize', position);
    return () => window.removeEventListener('resize', position);
  }, [moreOpen]);
  useEffect(() => () => clearTimeout(moreHoverTimer.current), []);
  useEffect(() => {
    if (!moreOpen) return;
    const dismiss = event => { if (!moreRef.current?.contains(event.target) && !morePanelRef.current?.contains(event.target)) setMoreOpen(false); };
    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [moreOpen]);
  const [saved, setSaved] = useState([]);
  const navigate = useNavigate();
  const { user, openAuthModal, signOut } = useAuth();

  useEffect(() => {
    const onSaved = () => setSaved(getSavedStories());
    onSaved();
    window.addEventListener("std-saved-changed", onSaved);
    return () => window.removeEventListener("std-saved-changed", onSaved);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setDrawerOpen(false);
        setAccountOpen(false);
        setMoreOpen(false);
        clearTimeout(moreHoverTimer.current);
        if (moreRef.current?.contains(document.activeElement) || morePanelRef.current?.contains(document.activeElement)) moreTriggerRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const goSubscribe = () => {
    setAccountOpen(false);
    setDrawerOpen(false);
    setSubscribeOpen(true);
  };

  const savedStories = saved.map(getStoryById).filter(Boolean);

  const [prefsEmail, setPrefsEmail] = useState("");
  const [digests, setDigests] = useState({ daily: true, breaking: false, sports: false });
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [prefsMsg, setPrefsMsg] = useState("");

  useEffect(() => {
    if (!accountOpen) return undefined;
    let initial = "";
    try {
      const list = JSON.parse(localStorage.getItem("std-newsletter") || "[]");
      if (Array.isArray(list) && list.length) initial = list[0];
    } catch (e) {}
    if (initial) setPrefsEmail(initial);
    return undefined;
  }, [accountOpen]);

  /* Keep the toggles in sync with the email actually typed — not just the
     first-time-opened localStorage address. */
  useEffect(() => {
    const email = prefsEmail.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return undefined;
    let live = true;
    const t = setTimeout(() => {
      fetchNewsletterPrefs(email).then((d) => {
        if (live && d) setDigests({ daily: true, breaking: false, sports: false, ...d });
      });
    }, 400);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [prefsEmail]);

  const savePrefs = async () => {
    const email = prefsEmail.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setPrefsMsg("Please enter a valid email address.");
      return;
    }
    setPrefsSaving(true);
    setPrefsMsg("");
    const result = await saveNewsletterPrefs(email, digests);
    setPrefsSaving(false);
    if (result && !result.error) {
      try {
        const list = JSON.parse(localStorage.getItem("std-newsletter") || "[]");
        if (!list.includes(email))
          localStorage.setItem("std-newsletter", JSON.stringify([email, ...list].slice(0, 3)));
      } catch (e) {}
      setPrefsMsg(result.message || "Preferences saved.");
    } else {
      setPrefsMsg((result && result.error) || "Something went wrong. Please try again.");
    }
  };

  return (
    <>
    <div ref={sentinelRef} aria-hidden="true" className="h-px -mb-px" />
    <header
      ref={headerRef}
      data-testid="masthead"
      className={`sticky top-0 xl:relative xl:top-auto z-50 bg-[#cf0a0a] text-white transition-shadow duration-300 ${compact ? "shadow-[0_4px_14px_rgba(0,0,0,0.16)]" : ""}`}
    >
      <div className={`relative mx-auto w-full max-w-[1640px] px-4 sm:px-[26px] flex items-center gap-3 transition-[min-height] duration-300 motion-reduce:transition-none ${compact ? "min-h-[54px] sm:min-h-[62px]" : "min-h-[64px] sm:min-h-[90px]"}`}>
        <button ref={mobileTriggerRef} onClick={() => toggleMenu()} aria-label={drawerOpen ? "Close menu" : "Open menu"} aria-expanded={drawerOpen} aria-controls="standard-navigation-curtain" data-testid="masthead-hamburger" className="shrink-0 xl:hidden h-11 w-11 flex items-center justify-center"><MenuIcon open={drawerOpen}/></button>

        <Link
          to="/"
          aria-label="The Standard — home"
          data-testid="masthead-logo-link"
          className={`absolute left-1/2 -translate-x-1/2 xl:relative xl:left-auto xl:translate-x-0 font-blackletter leading-none whitespace-nowrap transition-[font-size,letter-spacing] duration-300 motion-reduce:transition-none ${compact ? "text-[clamp(18px,5.5vw,23px)] sm:text-[28px] tracking-normal" : "text-[26px] sm:text-[34px] tracking-[1px]"}`}
        >
          The Standard
        </Link>

        {/* Centered navigation (desktop) */}
        <nav
          aria-label="Main navigation"
          className="absolute left-1/2 -translate-x-1/2 hidden xl:flex items-center gap-6"
        >
          <button ref={triggerRef} type="button" onClick={() => toggleMenu()} aria-label={drawerOpen ? "Close menu" : "Open menu"} aria-expanded={drawerOpen} aria-controls="standard-navigation-curtain" className="flex h-9 w-9 items-center justify-center hover:bg-white/10"><MenuIcon open={drawerOpen}/></button>
          {NAV.map((item) => (
            <Link
              key={item.label}
              to={item.href}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`whitespace-nowrap text-[15px] xl:text-[17px] font-medium transition-colors duration-150 hover:text-[#eeee22] ${
                item.active ? "text-[#eeee22]" : "text-white"
              }`}
            >
              {item.label}
            </Link>
          ))}
          <div ref={moreRef} className="relative"
            onMouseEnter={() => { clearTimeout(moreHoverTimer.current); moreHoverTimer.current = setTimeout(() => setMoreOpen(true), 160); }}
            onMouseLeave={closeMoreSoon}
            onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget) && !morePanelRef.current?.contains(event.relatedTarget)) setMoreOpen(false); }}>
            <button ref={moreTriggerRef} type="button" data-testid="nav-more-button" aria-expanded={moreOpen} aria-controls="masthead-more-links"
              onClick={() => { clearTimeout(moreHoverTimer.current); setMoreOpen(open => !open); }}
              onKeyDown={event => { if (event.key === 'ArrowDown') { event.preventDefault(); setMoreOpen(true); requestAnimationFrame(() => morePanelRef.current?.querySelector('a')?.focus()); } }}
              className="flex items-center gap-1 py-3 text-[15px] xl:text-[17px] font-medium hover:text-[#eeee22] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white">
              More <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" className={`transition-transform duration-300 ${moreOpen ? "rotate-180" : ""}`}><path d="m6 9 6 6 6-6"/></svg>
            </button>

          </div>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent("std-open-search"))}
            ref={searchRef}
            aria-label="Open search and menu" aria-expanded={drawerOpen} aria-controls="standard-navigation-curtain"
            data-testid="nav-search-button"
            className="p-2 hover:text-[#eeee22] transition-colors duration-150"
          >
            <SearchIcon />
          </button>
        </nav>

        {/* Right: account + subscribe */}
        <div className="ml-auto hidden xl:flex shrink-0 items-center gap-6">
          <button
            onClick={() => setAccountOpen((o) => !o)}
            data-testid="masthead-my-account"
            className="whitespace-nowrap text-[14px] font-medium hover:text-[#eeee22] transition-colors flex items-center gap-2"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="7" r="4"/><path d="M4 21v-2a8 8 0 0 1 16 0v2H4z"/></svg>
            My Account
          </button>
          <button
            onClick={goSubscribe}
            data-testid="masthead-subscribe"
            className="rounded-[4px] bg-white px-4 py-[10px] text-[11px] font-bold text-[#14245d] hover:bg-[#eeee22] transition-colors"
          >
            SUBSCRIBE
          </button>

          {/* Account panel */}
          <AnimatePresence>
            {accountOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } }}
                exit={{ height: 0, opacity: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                data-testid="account-panel"
                className="absolute right-[26px] top-full w-[380px] max-w-[calc(100vw-32px)] max-h-[calc(100dvh-140px)] overflow-hidden rounded-b-[12px] rounded-t-none bg-white text-[#141618] border border-neutral-200/80 shadow-[0_16px_60px_rgba(0,0,0,0.18)] z-50"
              >
                <div className="max-h-[calc(100dvh-140px)] overflow-y-auto p-6">
                <div className="flex items-center justify-between">
                  <p className="text-[22px] font-bold tracking-[-0.02em] text-[#141618]">
                    My Account
                  </p>
                  <button
                    onClick={() => setAccountOpen(false)}
                    aria-label="Close account panel"
                    data-testid="account-panel-close"
                    className="rounded-full bg-[#f3f4f5] p-2 text-neutral-500 hover:text-[#cf0a0a] transition-colors cursor-pointer"
                  >
                    <X size={16} />
                  </button>
                </div>

                {user ? (
                  <div
                    className="mt-4 flex items-center gap-3 pb-4 border-b border-[var(--line)]"
                    data-testid="account-user"
                  >
                    {user.picture ? (
                      <img
                        src={user.picture}
                        alt=""
                        data-testid="account-user-avatar"
                        className="w-10 h-10 rounded-full object-cover shrink-0"
                      />
                    ) : (
                      <span
                        data-testid="account-user-monogram"
                        className="w-10 h-10 rounded-full bg-[#cf0a0a] text-white flex items-center justify-center text-[13px] font-extrabold shrink-0"
                      >
                        {initialsOf(user.name || user.email)}
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <p data-testid="account-user-name" className="text-[14px] font-semibold truncate">
                        {user.name}
                      </p>
                      <p data-testid="account-user-email" className="text-[11px] text-[#858b92] truncate">
                        {user.email}
                      </p>
                    </div>
                    <button
                      onClick={signOut}
                      data-testid="account-signout-button"
                      className="shrink-0 border border-neutral-300 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-neutral-500 hover:border-[#cf0a0a] hover:text-[#cf0a0a] transition-colors"
                    >
                      Sign out
                    </button>
                  </div>
                ) : (
                  <div className="mt-5 rounded-[8px] bg-[#f6f7f8] p-4">
                    <p className="text-[14px] font-bold">Your Standard, in one place</p>
                    <p className="mt-1 mb-4 text-[12px] leading-[1.5] text-[#70757c]">Keep your saved stories and choose the updates you want.</p>
                    <button
                      onClick={openAuthModal}
                      data-testid="account-signin-button"
                      className="w-full rounded-[6px] bg-[#cf0a0a] text-white py-3 text-[13px] font-bold hover:bg-[#141618] transition-colors cursor-pointer"
                    >
                      Sign in / Create account
                    </button>
                    <p className="mt-2 text-center text-[11px] text-[#858b92]">
                      New here? Create an account
                    </p>
                  </div>
                )}

                <div className="mt-6">
                  <p className="flex items-center gap-2 text-[14px] font-bold text-[#141618]">
                    <Bookmark size={16} fill={savedStories.length ? "currentColor" : "none"} className={savedStories.length ? "text-[#cf0a0a]" : "text-[#141618]"} /> Saved Stories <span className="ml-auto rounded-full bg-[#f1f2f3] px-2 py-0.5 text-[11px] text-[#70757c]">{savedStories.length}</span>
                  </p>
                  {savedStories.length === 0 ? (
                    <p className="mt-2 text-[12px] leading-relaxed text-[#70757c]" data-testid="account-saved-empty">
                      No saved stories yet — tap the bookmark on any story to keep it here.
                    </p>
                  ) : (
                    <ul className="mt-3 max-h-[190px] overflow-y-auto divide-y divide-[#eceef0]" data-testid="account-saved-list">
                      {savedStories.map((s) => (
                        <li key={s.id} className="flex items-start justify-between gap-3 py-3">
                          <Link
                            to={`/${s.id}`}
                            onClick={() => setAccountOpen(false)}
                            className="min-w-0 flex-1 text-[13px] leading-[1.4] font-medium line-clamp-2 hover:text-[#cf0a0a] transition-colors"
                          >
                            {titleCaseHeadline(s.title)}
                          </Link>
                          <button
                            onClick={() => removeSavedStory(s.id)}
                            aria-label={`Remove ${titleCaseHeadline(s.title)} from saved`}
                            data-testid={`account-saved-remove-${s.id}`}
                            className="shrink-0 p-1 text-neutral-300 hover:text-[#cf0a0a] transition-colors"
                          >
                            <X size={13} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="mt-6 pt-5 border-t border-[#eceef0]">
                  <p className="flex items-center gap-2 text-[14px] font-bold text-[#141618]">
                    <MailCheck size={16} /> Your Newsletters
                  </p>
                  <input
                    type="email"
                    value={prefsEmail}
                    onChange={(e) => setPrefsEmail(e.target.value)}
                    placeholder="Email address" aria-label="Newsletter email address"
                    data-testid="prefs-email-input"
                    className="mt-4 w-full rounded-[6px] border border-[#e1e4e8] bg-[#fafbfc] px-3 py-3 text-[13px] text-[#141618] outline-none focus:border-[#cf0a0a] transition-colors"
                  />
                  <div className="mt-2">
                    {DIGESTS.map(({ key, label, hint }) => (
                      <button
                        type="button"
                        key={key}
                        onClick={() => setDigests((d) => ({ ...d, [key]: !d[key] }))}
                        aria-pressed={digests[key]}
                        data-testid={`prefs-toggle-${key}`}
                        className="w-full flex items-center justify-between gap-3 py-3 text-left cursor-pointer"
                      >
                        <span>
                          <span className="block text-[13px] font-medium text-[#141414]">{label}</span>
                          <span className="block text-[11px] text-[#858b92]">{hint}</span>
                        </span>
                        <span
                          className={`relative w-9 h-5 rounded-full transition-colors duration-200 shrink-0 ${
                            digests[key] ? "bg-[#cf0a0a]" : "bg-neutral-300"
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all duration-200 ${
                              digests[key] ? "left-[18px]" : "left-0.5"
                            }`}
                          />
                        </span>
                      </button>
                    ))}
                  </div>
                  {prefsMsg && (
                    <p
                      data-testid="prefs-status"
                      className="mt-2 text-[12px] font-semibold text-[#141414]"
                    >
                      {prefsMsg}
                    </p>
                  )}
                  <button
                    onClick={savePrefs}
                    disabled={prefsSaving}
                    data-testid="prefs-save-button"
                    className="mt-4 inline-flex w-full justify-center items-center gap-2 rounded-[6px] bg-[#141618] text-white px-4 py-3 text-[13px] font-bold hover:bg-[#cf0a0a] disabled:opacity-60 transition-colors cursor-pointer"
                  >
                    {prefsSaving ? "Saving…" : "Save preferences"}
                  </button>
                </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="ml-auto flex shrink-0 items-center xl:hidden">
          {compact && <button type="button" onClick={toggleCompactTheme} aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} data-testid="sticky-theme-toggle" className="flex h-11 w-8 items-center justify-center hover:text-[#eeee22] transition-colors">{theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}</button>}
          <button ref={mobileSearchRef} onClick={() => toggleMenu(true)} aria-label="Open search and menu" aria-expanded={drawerOpen} aria-controls="standard-navigation-curtain" className={`flex h-11 shrink-0 items-center justify-center ${compact ? "w-8" : "w-11"}`}><SearchIcon/></button>
        </div>
      </div>

      <div ref={morePanelRef} id="masthead-more-links" data-testid="nav-more-mega-menu" aria-hidden={!moreOpen} inert={!moreOpen}
        style={{left: moreLeft}} onMouseEnter={() => clearTimeout(moreHoverTimer.current)} onMouseLeave={closeMoreSoon}
        onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget) && !moreRef.current?.contains(event.relatedTarget)) setMoreOpen(false); }}
        className={`more-menu-curtain absolute top-full z-50 hidden w-[540px] max-w-[calc(100vw-32px)] text-[var(--ink)] xl:grid ${moreOpen ? "is-open" : ""}`}>
        <div className="min-h-0 overflow-hidden">
          <div className="max-h-[calc(100dvh-140px)] overflow-y-auto rounded-b-[8px] border border-t-0 border-[var(--line)] bg-[var(--bg)] shadow-[0_18px_55px_rgba(0,0,0,0.18)]">
              <div className="grid grid-cols-2 p-6" aria-label="More navigation">
                <section className="min-w-0 border-r border-[var(--line)] px-2 py-3" aria-labelledby="more-sections-heading">
                  <h2 id="more-sections-heading" className="mb-3 px-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#969696]">Explore</h2>
                  {NAV_MORE.map(label => <Link key={label} to={`/category/${label.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setMoreOpen(false)} className="block rounded-[5px] px-3 py-2.5 text-[15px] font-medium leading-5 transition-colors hover:bg-[#cf0a0a]/[0.07] hover:text-[#cf0a0a] focus-visible:bg-[#cf0a0a]/[0.07] focus-visible:text-[#cf0a0a]">{label}</Link>)}
                </section>
                <section className="min-w-0 px-2 py-3" aria-labelledby="more-company-heading">
                  <h2 id="more-company-heading" className="mb-3 px-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#969696]">The Standard</h2>
                  {companyNavigation.map(item => <Link key={item.href} to={item.href} onClick={() => setMoreOpen(false)} className="block rounded-[5px] px-3 py-2.5 text-[15px] font-medium leading-5 transition-colors hover:bg-[#cf0a0a]/[0.07] hover:text-[#cf0a0a] focus-visible:bg-[#cf0a0a]/[0.07] focus-visible:text-[#cf0a0a]">{item.label}</Link>)}
                </section>
              </div>
          </div>
        </div>
      </div>

      <NavigationCurtain open={drawerOpen} top={curtainTop} onClose={closeMenu} triggerRef={triggerRef} searchRef={searchRef} mobileTriggerRef={mobileTriggerRef} mobileSearchRef={mobileSearchRef} focusSearch={focusSearch}/>
    </header>
    {pathname !== "/" && <AdSlot placement="masthead-banner" />}
    <SubscribeModal open={subscribeOpen} onClose={closeSubscribe} />
    </>
  );
};

export default Masthead;
